import React from 'react';

import { CheckResult, CheckStatus } from '../types';
import { getInstructionsForCheck } from './InstructionsPanel';

interface CheckItemProps {
  check: CheckResult;
}

const statusConfig: Record<
  CheckStatus,
  { icon: string; label: string; classes: string; instructionsClasses?: string }
> = {
  passed: {
    icon: 'OK',
    label: 'Aprovado',
    classes: 'text-green-700 bg-green-50 border-green-200',
  },
  failed: {
    icon: 'X',
    label: 'Bloqueado',
    classes: 'text-red-700 bg-red-50 border-red-200',
    instructionsClasses: 'border-red-200 bg-white/80 text-red-900',
  },
  warning: {
    icon: '!',
    label: 'Atenção',
    classes: 'text-yellow-700 bg-yellow-50 border-yellow-200',
    instructionsClasses: 'border-yellow-200 bg-white/80 text-yellow-900',
  },
  skipped: {
    icon: '-',
    label: 'Ignorado',
    classes: 'text-gray-500 bg-gray-50 border-gray-200',
  },
};

export function CheckItem({ check }: CheckItemProps) {
  const config = statusConfig[check.status];
  const instructions =
    check.status === 'failed' || check.status === 'warning'
      ? getInstructionsForCheck(check)
      : [];

  return (
    <div className={`rounded-lg border p-4 ${config.classes}`} role="listitem">
      <div className="flex items-start gap-3">
        <span
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-sm font-bold"
          aria-hidden="true"
        >
          {config.icon}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{check.checkName}</p>
          <p className="mt-0.5 break-words text-sm opacity-80">
            {check.details}
          </p>
        </div>
        <span className="shrink-0 text-xs font-medium opacity-70">
          {config.label}
        </span>
      </div>

      {instructions.length > 0 && (
        <div
          className={`mt-4 rounded-lg border p-4 ${config.instructionsClasses}`}
        >
          <p className="mb-2 text-sm font-semibold">Como resolver</p>
          <ol className="space-y-1.5">
            {instructions.map((step, idx) => (
              <li key={step} className="flex gap-2 text-sm">
                <span className="shrink-0 font-bold">{idx + 1}.</span>
                <span className="break-words">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export function CheckItemSkeleton() {
  return (
    <div className="animate-pulse rounded-lg border border-gray-200 bg-gray-50 p-4">
      <div className="flex items-start gap-3">
        <div className="h-6 w-6 shrink-0 rounded-full bg-gray-300" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-1/3 rounded bg-gray-300" />
          <div className="h-3 w-2/3 rounded bg-gray-200" />
        </div>
      </div>
    </div>
  );
}
